import { Component, OnInit, inject, ViewChild, TemplateRef, effect, signal, computed, ChangeDetectionStrategy, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BillingService } from '../../services/billing.service';
import { PeriodService, periodKey } from '../../services/period.service';
import { CatalogService } from '@services/data/catalog.service';
import { EntityBillingService } from '../../services/entity-billing.service';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Workbook } from 'exceljs';
import { AdvancedFilterComponent, FilterField } from 'app/core/components/advanced-filter/advanced-filter.component';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { AsinetExportService, AsinetRow } from '@services/export/asinet-export.service';
import { AuthService } from '@services/core/auth.service';
import { PermissionService } from '@services/core/permission.service';

@Component({
  selector: 'app-billing-management',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, AdvancedFilterComponent, ListCardComponent, PageHeaderComponent, MobileFooterComponent],
  templateUrl: './billing-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./billing-management.component.scss']
})

export class BillingManagementComponent implements OnInit {
  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  // Signals para datos base
  periods = signal<any[]>([]);
  allEntities = signal<any[]>([]);
  billingsRaw = signal<any[]>([]);
  isLoading = signal<boolean>(true);
  hasLoadedPeriod = signal<boolean>(false);
  apsMappings = signal<Record<number, any>>({});
  isEditingAps = signal<boolean>(false);
  tempApsMappings = signal<Record<number, any>>({});
  backupApsMappings = signal<Record<number, any>>({});

  // Signals para cobro a entidades
  entityBillingItems = signal<any[]>([]);
  entityBillingTotal = signal<number>(0);
  isEntityBillingLoading = signal<boolean>(false);

  /** Periodo cerrado del modal ASINET (independiente del rango de la página). */
  asinetPeriodId = signal<number | null>(null);
  closedPeriods = computed(() => this.periodService.closedBillingPeriods());
  asinetPeriod = computed(() => this.periods().find((p) => p.id === this.asinetPeriodId()) ?? null);
  asinetPartyAll = signal<any[]>([]);
  isAsinetPartyLoading = signal<boolean>(false);
  asinetEntityCode = signal<number | null>(null);

  /** Personas de la entidad elegida. Cada fila es independiente. */
  asinetPartyBillings = computed(() => {
    const code = this.asinetEntityCode();
    const all = this.asinetPartyAll();
    if (code == null) return all;
    return all.filter((b) => Number(b.entityCode) === Number(code));
  });

  asinetPartyIsAccounted = computed(() => {
    const rows = this.asinetPartyBillings();
    return rows.length > 0 && rows.every((b) => !!b.isAccounted);
  });

  // Signals para filtros (Model)
  selectedPeriodStartId = signal<number | null>(null);
  selectedPeriodEndId = signal<number | null>(null);
  selectedEntityCode = signal<any[]>([]);
  selectedPartyRut = signal<string | null>(null);
  selectedAccounted = signal<string>('');

  accountedFilters = [
    { slug: '', name: 'Todos' },
    { slug: 'false', name: 'Pendiente de contabilizar' },
    { slug: 'true', name: 'Contabilizado' }
  ];

  // Configuración y estado de filtros avanzados
  advancedFilterFields: FilterField[] = [
    { key: 'entityCode', label: 'Entidad' },
    { key: 'plate', label: 'Patente' },
    { key: 'party', label: 'Socio / RUT' },
    { key: 'amount', label: 'Prima' },
    { key: 'commercialValue', label: 'Valor Comercial' }
  ];
  activeAdvancedFilters = signal<Array<{ field: string; value: string }>>([]);

  selectedPeriod = computed(() => this.periods().find(p => p.id === this.selectedPeriodStartId()) ?? null);
  isSinglePeriodSelected = computed(() => this.selectedPeriodStartId() === this.selectedPeriodEndId());
  isPeriodActive = computed(() => this.selectedPeriod()?.statusSlug === 'ACTIVE');
  isPeriodClosed = computed(() => this.selectedPeriod()?.statusSlug === 'INACTIVE');

  entityAccountedInfo = computed(() => {
    const p = this.asinetPeriod() ?? this.selectedPeriod();
    if (!p?.isEntityAccounted) return null;
    const user = p.entityAccountedUser?.displayName ?? p.entityAccountedUser?.mail ?? 'Usuario';
    const date = p.entityAccountedAt ? new Date(p.entityAccountedAt).toLocaleDateString() : '';
    return { user, date };
  });

  filteredBillings = computed(() => {
    let data = this.billingsRaw();
    const entityCodes = this.selectedEntityCode();
    const rut = this.selectedPartyRut();
    const accounted = this.selectedAccounted();
    const advFilters = this.activeAdvancedFilters();

    if (entityCodes?.length > 0) {
      data = data.filter(b => entityCodes.includes(b.entityCode));
    }
    if (rut) {
      data = data.filter(b => b.party?.rut === rut);
    }
    if (accounted === 'true') {
      data = data.filter(b => this.isBillingPartyAccounted(b));
    } else if (accounted === 'false') {
      data = data.filter(b => !this.isBillingPartyAccounted(b));
    }
    if (advFilters.length > 0) {
      data = this._applyAdvancedBillingFilters(data, advFilters);
    }
    return data;
  });

  // Aplica reglas dinámicas de filtro avanzado sobre primas
  private _applyAdvancedBillingFilters(data: any[], filters: Array<{ field: string; value: string }>): any[] {
    return data.filter((item) => filters.every((f) => this._matchBillingRule(item, f)));
  }

  // Comprueba si un registro de prima cumple la regla del filtro
  private _matchBillingRule(item: any, rule: { field: string; value: string }): boolean {
    const val = rule.value.toLowerCase();
    if (rule.field === 'entityCode') return String(item.entityCode ?? '').toLowerCase().includes(val);
    if (rule.field === 'plate') return String(item.vehicle?.plate ?? '').toLowerCase().includes(val);
    if (rule.field === 'party') {
      return `${item.party?.names ?? ''} ${item.party?.rut ?? ''}`.toLowerCase().includes(val);
    }
    if (rule.field === 'amount') return String(item.amount ?? '').includes(val);
    if (rule.field === 'commercialValue') return String(item.vehicle?.commercialValue ?? '').includes(val);
    return true;
  }

  parties = computed(() => {
    const data = this.billingsRaw();
    const entities = this.selectedEntityCode();
    
    const filteredByEntity = entities?.length > 0 
      ? data.filter(b => entities.includes(b.entityCode))
      : data;

    const pMap = new Map();
    filteredByEntity.forEach(b => {
      if (b.party?.rut) pMap.set(b.party.rut, { rut: b.party.rut, name: b.party.names });
    });
    return Array.from(pMap.values());
  });

  // Computed para entidades disponibles
  entities = computed(() => {
    const all = this.allEntities();
    if (!this.hasLoadedPeriod()) return all;
    
    const billingCodes = new Set(this.billingsRaw().map(b => b.entityCode));
    return all.filter(e => billingCodes.has(e.code));
  });

  // Computed para totales
  totalAmount = computed(() => {
    return this.filteredBillings().reduce((sum, bill) => sum + (Number(bill.amount) || 0), 0);
  });

  filteredEndPeriods: any[] = [];
  
  pdfSrc!: SafeResourceUrl;
  @ViewChild('preview') preview!: TemplateRef<any>;

  private billingService = inject(BillingService);
  private entityBillingService = inject(EntityBillingService);
  private periodService = inject(PeriodService);
  private catalogService = inject(CatalogService);
  readonly authService = inject(AuthService);
  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);
  private asinetExport = inject(AsinetExportService);

  ngOnInit(): void {
    if (!this.periodService.periods().length) this.periodService.reload();
  }

  private lastLoadedKey = '';

  constructor() {
    effect(() => {
      // Solo entidades del rol activo (user_role_entity), no el catálogo completo
      const assigned = (this.authService.sharingEntities() as any[]) || [];
      const catalog = this.catalogService.entities() || [];
      const byCode = new Map(catalog.map((e: any) => [Number(e.code), e]));
      this.allEntities.set(
        assigned
          .map((e: any) => {
            const code = Number(e.code ?? e.entityCode);
            if (!Number.isFinite(code)) return null;
            const cat = byCode.get(code);
            const name = cat?.name ?? e.name ?? '';
            return { ...(cat || {}), ...e, code, name: `${code} - ${name}` };
          })
          .filter(Boolean) as any[]
      );
    });
    effect(() => {
      const list = this.periodService.billingPeriods();
      if (!list.length) return;
      untracked(() => this.applyPeriods(list));
    });
  }

  // Aplica periodos: por defecto enero del año del último cerrado → último cerrado
  applyPeriods(processedPeriods: any[]) {
    this.periods.set(processedPeriods);
    if (!processedPeriods.length) {
      this.isLoading.set(false);
      this.hasLoadedPeriod.set(true);
      return;
    }
    const range = this.periodService.defaultClosedYearRangeIds();
    const startId = range?.startId ?? this.periodService.activePeriod()?.id ?? processedPeriods[0].id;
    const endId = range?.endId ?? startId;
    const isSame = this.selectedPeriodStartId() === startId && this.selectedPeriodEndId() === endId;
    this.selectedPeriodStartId.set(startId);
    this.selectedPeriodEndId.set(endId);
    this.updateFilteredEndPeriods();
    if (!isSame || !this.hasLoadedPeriod()) this.loadBillings(true);
  }

  updateFilteredEndPeriods() {
    const start = this.periods().find((p) => p.id === this.selectedPeriodStartId());
    const startValue = periodKey(start);
    this.filteredEndPeriods = this.periods().filter((p) => periodKey(p) >= startValue);

    const end = this.periods().find((p) => p.id === this.selectedPeriodEndId());
    if (end && periodKey(end) < startValue) {
      this.selectedPeriodEndId.set(this.selectedPeriodStartId());
    }
  }



  isSelectedPeriodOpen(): boolean {
    if (!this.selectedPeriodStartId()) return false;
    const period = this.periods().find(p => p.id === this.selectedPeriodStartId());
    return period?.statusSlug === 'ACTIVE';
  }


  private _validatedPeriod(){
    return this.selectedPeriodStartId() && this.selectedPeriodEndId();
  }



  // Consulta primas para el rango de periodos seleccionado evitando duplicados
  loadBillings(refreshEntities = false) {
    if (!this._validatedPeriod()) return;
    const key = `${this.selectedPeriodStartId()}-${this.selectedPeriodEndId()}`;
    if (this.isLoading() && this.lastLoadedKey === key && this.hasLoadedPeriod()) return;
    this.lastLoadedKey = key;
    this.updateFilteredEndPeriods();
    if (refreshEntities) this.selectedEntityCode.set([]);
    this.isLoading.set(true);
    const f: any = { periodStartId: this.selectedPeriodStartId(), periodEndId: this.selectedPeriodEndId() };
    this.billingService.list(f).subscribe({
      next: (res) => {
        this.billingsRaw.set(res);
        this.loadApsMappings();
        if (refreshEntities) this.hasLoadedPeriod.set(true);
        this.isLoading.set(false);
      },
      error: (err) => { 
        console.error(err); 
        this.hasLoadedPeriod.set(true);
        this.isLoading.set(false); 
      }
    });
  }

  applyFilters() {
    const parties = this.parties();
    if (this.selectedPartyRut() && !parties.find(p => p.rut === this.selectedPartyRut())) {
      this.selectedPartyRut.set(null);
    }
  }

  loadApsMappings(forceSync = false) {
    const ids = Array.from(new Set(this.billingsRaw().map(b => b.party?.id).filter(id => !!id)));
    if (ids.length === 0) return;
    this.billingService.getApsMappings(ids, forceSync).subscribe(res => {
        if (forceSync) {
            this.tempApsMappings.set({ ...res });
            Swal.fire({ title: 'Sincronizado', text: 'Se obtuvieron sugerencias de APS. Revise y guarde los cambios.', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 4000 });
        } else {
            this.apsMappings.set(res);
        }
    });
  }

  syncWithAps() {
    Swal.fire({
      title: 'Sincronizando con APS...',
      text: 'Extrayendo las subcuentas desde APS.',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); }
    });

    this.billingService.syncApsDatabase().subscribe({
      next: () => {
        Swal.close();
        if (this.asinetPartyBillings().length) {
          this.loadApsMappingsForAsinet(this.asinetPartyBillings());
        } else {
          this.loadApsMappings(true);
        }
      },
      error: (err) => {
        Swal.fire('Error', 'Fallo al sincronizar con APS: ' + (err.error?.error || err.message), 'error');
      }
    });
  }



  openApsModal(content: any) {
    if (!this.perm.canCurrent('canUpdate') && !this.perm.canCurrent('canCreate')) return;
    if (!this._initAsinetPeriodOrWarn()) return;
    this._ensureAsinetEntity();
    this.isEditingAps.set(false);
    this.modalService.open(content, { size: 'lg', backdrop: 'static', centered: true });
    this.loadAsinetPartyBillings();
  }

  /** Inicializa el periodo cerrado del modal ASINET. */
  private _initAsinetPeriodOrWarn(): boolean {
    const closedId = this.periodService.latestClosedPeriodId();
    if (!closedId) {
      Swal.fire('Atención', 'No hay periodos cerrados para contabilizar en ASINET.', 'info');
      return false;
    }
    this.asinetPeriodId.set(closedId);
    return true;
  }

  // Cambia el periodo cerrado del modal de cobros personas
  onAsinetPartyPeriodChange(periodId: number | null): void {
    this.asinetPeriodId.set(periodId);
    this.isEditingAps.set(false);
    this.loadAsinetPartyBillings();
  }

  onAsinetPartyEntityChange(entityCode: number | null): void {
    this.asinetEntityCode.set(entityCode);
    this.isEditingAps.set(false);
  }

  private _ensureAsinetEntity(): void {
    const list = this.allEntities();
    const current = this.asinetEntityCode();
    if (current != null && list.some((e) => Number(e.code) === Number(current))) return;
    this.asinetEntityCode.set(list[0] ? Number(list[0].code) : null);
  }

  // Carga primas del periodo (URE). El filtro de entidad es solo de vista.
  loadAsinetPartyBillings(): void {
    const periodId = this.asinetPeriodId();
    if (!periodId) {
      this.asinetPartyAll.set([]);
      return;
    }
    this.isAsinetPartyLoading.set(true);
    this.billingService.list({ periodStartId: periodId, periodEndId: periodId }).subscribe({
      next: (res) => {
        this.asinetPartyAll.set(res ?? []);
        this._seedApsMappingsFromBillings(res ?? []);
        this.loadApsMappingsForAsinet(res ?? []);
        this.isAsinetPartyLoading.set(false);
      },
      error: () => {
        this.asinetPartyAll.set([]);
        this.isAsinetPartyLoading.set(false);
        Swal.fire('Error', 'No se pudieron cargar los cobros del periodo', 'error');
      }
    });
  }

  private _seedApsMappingsFromBillings(billings: any[]): void {
    const initialMappings: any = {};
    for (const bill of billings) {
      if (bill.party?.id) {
        if (bill.subAccount?.subAccountCode) {
          initialMappings[bill.party.id] = { code: bill.subAccount.subAccountCode, isSuggested: false, matchName: '' };
        } else {
          initialMappings[bill.party.id] = { code: '', isSuggested: true, matchName: '' };
        }
      }
    }
    this.tempApsMappings.set(initialMappings);
    this.backupApsMappings.set({ ...initialMappings });
  }

  private loadApsMappingsForAsinet(billings: any[]): void {
    const ids = Array.from(new Set(billings.map((b) => b.party?.id).filter((id) => !!id)));
    if (!ids.length) return;
    this.billingService.getApsMappings(ids, false).subscribe((res) => {
      const current = { ...this.tempApsMappings() };
      Object.keys(res || {}).forEach((k) => {
        if (!current[k]?.code) current[k] = res[k];
      });
      this.tempApsMappings.set(current);
      this.backupApsMappings.set({ ...current });
      this.apsMappings.set({ ...current });
    });
  }

  toggleEditAps() {
    if (this.isEditingAps()) {
      // Al cancelar, restauramos desde el respaldo
      this.tempApsMappings.set({ ...this.backupApsMappings() });
    }
    this.isEditingAps.set(!this.isEditingAps());
  }

  saveBulkApsMapping(modal: any) {
    const billings = this.asinetPartyBillings();
    const mappings = Object.entries(this.tempApsMappings()).map(([k, v]) => {
      const partyId = Number(k);
      const bill = billings.find(b => b.party?.id === partyId);
      return { partyId, subAccountCode: v?.code || '', entityCode: bill?.entityCode };
    });
    this.billingService.saveBulkApsMapping(mappings).subscribe({
      next: () => {
        this.apsMappings.set({ ...this.tempApsMappings() });
        this.backupApsMappings.set({ ...this.tempApsMappings() });
        Swal.fire({ title: 'Guardado', text: 'Cuentas configuradas correctamente', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        this.isEditingAps.set(false);
      },
      error: (e) => Swal.fire('Error', 'No se pudo guardar la configuración', 'error')
    });
  }

  getDate() {
    let date = new Date();
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' } as const;
    const formattedDate = date.toLocaleDateString('es-ES', options);
    const formattedTime = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${formattedDate} ${formattedTime}`;
  }

  getDomain() {
    const domian = window.location.hostname.split('.');
    return domian[0] + '.uch';
  }

  findEntityName(code: number): string {
    return this.entities().find(e => e.code === code)?.name ?? '';
  }

  findPeriodName(id: number): string {
    return this.periods().find(p => p.id === id)?.displayName ?? '';
  }

  /** Cobro de esa persona (party_billing.is_accounted). */
  isBillingPartyAccounted(bill: any): boolean {
    return !!(bill?.isAccounted);
  }

  billingPartyAccountedTitle(bill: any): string {
    return bill?.accountedAt ? String(bill.accountedAt) : '';
  }

  private toNumber(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  private addHeaderPDF(doc: jsPDF, entityName: string, periodName: string): number {
    let currentY = 35;
    autoTable(doc, {
      startY: currentY,
      head: [
        [{ content: 'Fondo de Protección Vehicular', styles: { fontStyle: 'normal', fontSize: 10 } }],
        [{ content: entityName, styles: { fontStyle: 'italic', fontSize: 10 } }]
      ],
      theme: 'plain',
      headStyles: { halign: 'center', cellPadding: 1, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 },
      tableWidth: 'auto',
      didDrawPage: (data) => { currentY = data.cursor?.y || currentY; }
    });

    autoTable(doc, {
      startY: currentY + 5,
      head: [
        [{ content: 'Listado de Primas Detallado', styles: { fontStyle: 'bold', fontSize: 11 } }],
        [{ content: `Periodo: ${periodName}`, styles: { fontStyle: 'bold', fontSize: 9 } }]
      ],
      theme: 'plain',
      headStyles: { halign: 'center', cellPadding: 0.3, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 },
      tableWidth: 'auto',
      didDrawPage: (data) => { currentY = data.cursor?.y ?? currentY; }
    });
    return currentY;
  }

  private addFooterPDF(doc: jsPDF, pdfWidth: number, pageNumber: number, totalPages: number) {
    autoTable(doc, {
      startY: 742,
      body: [['Impreso: ' + this.getDate(), 'Usuario: FPV', 'Sistema: ' + this.getDomain(), 'Página ' + pageNumber + ' de ' + totalPages]],
      theme: 'plain',
      bodyStyles: { fontSize: 7, cellPadding: 0.3, textColor: [0, 0, 0] },
      tableWidth: 'auto',
      didDrawPage: (data) => {
        doc.setLineWidth(0.5);
        doc.line(35, 737, pdfWidth, 737);
      },
    });
  }

  exportPDF() {
    const doc = new jsPDF('p', 'pt', 'letter');
    const pdfWidth = doc.internal.pageSize.getWidth() - 35;

    const periodName = `${this.findPeriodName(this.selectedPeriodStartId()!)} - ${this.findPeriodName(this.selectedPeriodEndId()!)}`;

    let entityName = 'Todas las Entidades';
    const entityCodes = this.selectedEntityCode();
    if (entityCodes && entityCodes.length > 0) {
      if (entityCodes.length === 1) {
        entityName = this.findEntityName(entityCodes[0]);
      } else {
        entityName = 'Varias Entidades (' + entityCodes.length + ')';
      }
    }

    const data = this.filteredBillings();
    const tableBody = data.map(bill => [
      bill.party?.entity?.code ?? '',
      bill.party?.names ?? '',
      `${bill.year}/${(bill.period ?? 0).toString().padStart(2, '0')}`,
      bill.vehicle?.plate || 'S/P',
      new Intl.NumberFormat('es-CL').format(this.toNumber(bill.vehicle?.commercialValue)),
      new Intl.NumberFormat('es-CL').format(this.toNumber(bill.amount))
    ]);

    autoTable(doc, {
      startY: 105,
      head: [['Entidad', 'Socio', 'Periodo', 'Patente', 'Valor', 'Prima']],
      body: tableBody,
      theme: 'plain',
      bodyStyles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0] },
      headStyles: {
        cellPadding: 2,
        fontStyle: 'bold',
        textColor: [0, 0, 0],
        lineWidth: { bottom: 0.5 },
        lineColor: [0, 0, 0]
      },
      columnStyles: {
        4: { halign: 'right' },
        5: { halign: 'right' }
      },
      showFoot: 'lastPage',
      foot: [[
        { content: 'TOTAL DEL PERIODO:', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(this.totalAmount()), styles: { halign: 'right', fontStyle: 'bold' } }
      ]],
      margin: { top: 105, bottom: 70 },
      styles: { fontSize: 8 }
    });

    const totalPages = doc.getNumberOfPages();
    for (let pageNumber = 1; pageNumber < totalPages + 1; pageNumber++) {
      doc.setPage(pageNumber);
      this.addHeaderPDF(doc, entityName, periodName);
      this.addFooterPDF(doc, pdfWidth, pageNumber, totalPages);
    }

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
    this.modalService.open(this.preview, { size: 'lg', backdrop: 'static' });
  }

  excelHeaders() {
    return [
      'Entidad',
      'Socio',      
      'Periodo',
      'Patente',
      'Valor',
      'Monto',
    ];
  }

  styleHeaderRow(worksheet: any, rowNumber: number): void {
    const headerRow = worksheet.getRow(rowNumber);
    worksheet.getRow(rowNumber).height = 25;
    headerRow.eachCell((cell: any) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFBFBFBF' }
      };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'medium' },
        bottom: { style: 'medium' },
        left: { style: 'medium' },
        right: { style: 'medium' }
      };
    });
  }

  styleDataRow(row: any, headers: string[], index: number, rowspan: number): void {
    for (let col = 1; col <= headers.length; col++) {
      const cell = row.getCell(col);
      cell.border = {
        top: { style: index === 0 ? 'medium' : 'thin' },
        bottom: { style: index === rowspan - 1 ? 'medium' : 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
      };
      if (col === 5 || col === 6) {
        cell.numFmt = '#,##0';
      }
    }
  }

  autoAdjustColumns(worksheet: any): void {
    worksheet.columns.forEach((column: any) => {
      let max = 10;
      column.eachCell({ includeEmpty: true }, (cell: any) => {
        const len = cell.value ? cell.value.toString().length : 10;
        if (len > max) max = len;
      });
      column.width = max + 2;
    });
  }

  styleSubtotalRows(worksheet: any, rowNumber: number): void {
    const subtotalRow = worksheet.getRow(rowNumber);
    subtotalRow.height = 25;
    subtotalRow.eachCell((cell: any) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFBFBFBF' }
      };
      cell.alignment = { vertical: 'middle', horizontal: 'right' };
      cell.border = {
        top: { style: 'medium' },
        bottom: { style: 'medium' },
        left: { style: 'medium' },
        right: { style: 'medium' }
      };
      cell.numFmt = '#,##0';
    });
  }

  addSubtotalRow(worksheet: any, startCol: number, rowNumber: number, mergeCol: number): void {
    const lastRowNumber = worksheet.lastRow.number;
    const subtotalRow = worksheet.addRow([]);
    if (mergeCol >= 1) {
      worksheet.mergeCells(subtotalRow.number, 1, subtotalRow.number, mergeCol);
      const labelCell = worksheet.getCell(subtotalRow.number, 1);
      labelCell.value = 'SUBTOTAL';
      labelCell.alignment = { horizontal: 'center', vertical: 'middle' };
      labelCell.font = { bold: true };
    }
    for (let col = startCol; col <= worksheet.columnCount; col++) {
      const colLetter = worksheet.getColumn(col).letter;
      const cell = subtotalRow.getCell(col);
      cell.value = {
        formula: `SUM(${colLetter}${rowNumber}:${colLetter}${lastRowNumber})`,
        result: null
      } as any;
      cell.font = { bold: true };
    }
  }

  addRowsToWorksheet(data: any[], worksheet: any, headers: string[]): void {
    data.forEach((bill, index) => {
      const rowValues = [
        bill.party?.entity?.code ?? '',
        bill.party?.names ?? '',
        `${bill.year}/${(bill.period ?? 0).toString().padStart(2, '0')}`,
        bill.vehicle?.plate ?? 'S/P',
        bill.vehicle?.commercialValue ?? 0,
        bill.amount ?? 0,
      ];
      const row = worksheet.addRow(rowValues);
      this.styleDataRow(row, headers, index, data.length);
    });
  }

  createSheet(workbook: Workbook, title: string, headers: string[], data: any[]) {
    const safeTitle = title.substring(0, 31).replace(/[\\*?:/\[\]]/g, '');
    const worksheet = workbook.addWorksheet(safeTitle);
    worksheet.mergeCells(1, 1, 1, headers.length);
    const titleCell = worksheet.getCell('A1');
    titleCell.value = `${title.toUpperCase()}`;
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.font = { bold: true };
    worksheet.getRow(1).height = 40;

    for (let col = 1; col <= headers.length; col++) {
      worksheet.getCell(1, col).border = {
        top: { style: 'medium' },
        left: { style: 'medium' },
        bottom: { style: 'medium' },
        right: { style: 'medium' },
      };
    }

    worksheet.getRow(2).values = headers;
    this.styleHeaderRow(worksheet, 2);
    this.addRowsToWorksheet(data, worksheet, headers); 
    this.autoAdjustColumns(worksheet);
    this.addSubtotalRow(worksheet, 6, 3, 5);
    this.styleSubtotalRows(worksheet, worksheet.lastRow.number);
  }

  exportExcel(): void {
    const data = this.filteredBillings();
    if (!data || data.length === 0) return;
    const workbook = new Workbook();
    const periodName = `${this.findPeriodName(this.selectedPeriodStartId()!)} - ${this.findPeriodName(this.selectedPeriodEndId()!)}`;
    let entityName = 'Todas';
    const entityCodes = this.selectedEntityCode();
    if (entityCodes && entityCodes.length > 0) {
        if (entityCodes.length === 1) {
            entityName = this.findEntityName(entityCodes[0]);
        } else {
            entityName = 'Varias';
        }
    }
    this.createSheet(workbook, 'Cobros ' + periodName + ' ' + entityName, this.excelHeaders(), data);
    
    workbook.xlsx.writeBuffer().then(buffer => {
      const dateStr = this.getDate().replace(/[: /]/g, '');
      this.saveExcelFile(buffer, `Cobros_${periodName}_${dateStr}`);
    });
  }

  private saveExcelFile(buffer: any, fileName: string): void {
    this.asinetExport.saveExcelFile(buffer, fileName);
  }



    exportAsinetPartyVehicle(modalRef?: any) {
      if (!this.perm.canCurrent('canUpdate')) return;
      if (modalRef) modalRef.close();
      const data = this.asinetPartyBillings();
      const period = this.asinetPeriod();
      if (!data || data.length === 0 || !period) return;
      const periodName = period.displayName ?? this.findPeriodName(period.id);
      const rows: AsinetRow[] = data.map((m) => ({
        AccountCode: this.accountpartyFromList(m, data),
        SubAccountCode: this.tempApsMappings()[m.party?.id]?.code
          ?? this.apsMappings()[m.party?.id]?.code
          ?? '',
        FundCode: 10,
        FunctionCode: '0000',
        RestrictionCode: '0A',
        EntityValue: m.amount ?? 0,
        SendMemo: 'False',
        Description: `FPV ${this.asinetExport.monthName(m.period)} ${m.year}`,
        DueDate: '',
        Memo: m.party?.names ?? '',
        InvoiceNumber: '',
        InvoiceDate: '',
        CurrencyAmount: ''
      }));
      const dateStr = this.getDate().replace(/[: /]/g, '');
      this.asinetExport.download({
        sheetName: 'Análisis de Cuenta',
        rows,
        fileName: `Cobros_personas_${periodName}_${dateStr}`
      });
    }

    findPartyValue(party: any){
      return this.filteredBillings().find(x => x.party.uuid === party.uuid)?.entity.code;
    }


    accountparty(m: any): string {
      return this.accountpartyFromList(m, this.filteredBillings());
    }

    accountpartyFromList(m: any, list: any[]): string {
      const isIndependent = m.party?.groupSlug === 'INDEPENDENT';

      if (isIndependent) {
        const partyBillings = list
          .filter(b => b.party?.uuid === m.party?.uuid)
          .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0));

        if (m.id === partyBillings[0]?.id) {
          return '1135005';
        }
        return '1135001';
      }

      return '1135001';
    }

    exportAsinetEntity() {
      const items = this.entityBillingItems();
      const period = this.asinetPeriod();
      if (!items.length || !period) return;

      const rows: AsinetRow[] = [];
      items.forEach((item: any) => {
        const entity = this.catalogService.entities().find(e => Number(e.code) === Number(item.entityCode));
        const accountCode = (entity?.group === 'CORPORACION') ? '1136001' : '1136010';
        const description = `Cobro Prima FPV ${this.asinetExport.monthName(period.period)} ${period.year ?? ''}`.trim();
        const total = Number(item.amount) || 0;
        rows.push({
          AccountCode: String(accountCode),
          SubAccountCode: String(item.entityCode ?? ''),
          FundCode: 51,
          FunctionCode: '0000',
          RestrictionCode: '0A',
          EntityValue: total,
          SendMemo: 'False',
          Description: description
        });
        rows.push({
          AccountCode: '3188001',
          SubAccountCode: String(item.entityCode ?? ''),
          FundCode: 51,
          FunctionCode: '980312',
          RestrictionCode: '2',
          EntityValue: total * -1,
          SendMemo: 'False',
          Description: description
        });
      });

      const dateStr = this.getDate().replace(/[: /]/g, '');
      this.asinetExport.download({
        sheetName: 'Cobros Entidades',
        rows,
        fileName: `Cobros_entidades_${dateStr}`
      });
    }

    // Abre modal de cobro consolidado de entidades
    openEntityBillingModal(content: any) {
      if (!this.perm.canCurrent('canUpdate') && !this.perm.canCurrent('canCreate')) return;
      if (!this._initAsinetPeriodOrWarn()) return;
      this.isEntityBillingLoading.set(true);
      this.modalService.open(content, { size: 'lg', backdrop: 'static', centered: true });
      this.loadAsinetEntityBilling();
    }

    // Cambia el periodo cerrado del modal de entidades
    onAsinetEntityPeriodChange(periodId: number | null): void {
      this.asinetPeriodId.set(periodId);
      this.loadAsinetEntityBilling();
    }

    // Carga cobro consolidado del periodo cerrado
    loadAsinetEntityBilling(): void {
      const period = this.asinetPeriod();
      if (!period?.uuid) {
        this.entityBillingItems.set([]);
        this.entityBillingTotal.set(0);
        this.isEntityBillingLoading.set(false);
        return;
      }
      this.isEntityBillingLoading.set(true);
      this.entityBillingService.getByPeriod(period.uuid).subscribe({
        next: (res) => this._setEntityBillingData(res),
        error: () => this.isEntityBillingLoading.set(false)
      });
    }

    // Asigna datos de cobro a entidades
    private _setEntityBillingData(res: any) {
      this.entityBillingItems.set(res.items ?? []);
      this.entityBillingTotal.set(res.totalAmount ?? 0);
      this.isEntityBillingLoading.set(false);
    }

    // Exporta copia de Excel de cobro entidades
    exportEntitiesOnly(modalRef?: any) {
      if (!this.perm.canCurrent('canUpdate')) return;
      if (modalRef) modalRef.close();
      this.exportAsinetEntity();
    }

    // Confirma y contabiliza cobro a entidades
    exportAndAccountEntities(modalRef?: any) {
      if (!this.perm.canCurrent('canCreate')) return;
      const period = this.asinetPeriod();
      if (!period || period.statusSlug === 'ACTIVE') return this._alertMustBeClosed();
      Swal.fire(this._accountEntitiesConfirmOptions()).then((r) => {
        if (r.isConfirmed) this._executeAccountEntities(period, modalRef);
      });
    }

    // Opciones de confirmación para entidades
    private _accountEntitiesConfirmOptions() {
      return {
        title: '¿Exportar y Contabilizar?',
        text: 'Se generará el archivo ASINET y se marcará el cobro de entidades como contabilizado.',
        icon: 'question' as const,
        showCancelButton: true,
        confirmButtonText: 'Sí, Contabilizar',
        cancelButtonText: 'Cancelar'
      };
    }

    // Ejecuta contabilización de entidades
    private _executeAccountEntities(period: any, modalRef?: any) {
      if (modalRef) modalRef.close();
      this.exportAsinetEntity();
      this.entityBillingService.markAccounted(period.uuid).subscribe({
        next: () => this._onAccountSuccess('El cobro a entidades ha sido contabilizado.'),
        error: (e) => Swal.fire('Error', e.error?.error ?? 'Error al contabilizar', 'error')
      });
    }

    // Confirma y contabiliza cobro a personas
    exportAndAccountParties(modalRef?: any) {
      if (!this.perm.canCurrent('canCreate')) return;
      const period = this.asinetPeriod();
      const entityCode = this.asinetEntityCode();
      if (!period || period.statusSlug === 'ACTIVE') return this._alertMustBeClosed();
      if (!entityCode) {
        Swal.fire('Atención', 'Seleccioná la entidad a contabilizar', 'warning');
        return;
      }
      Swal.fire(this._accountPartiesConfirmOptions(entityCode)).then((r) => {
        if (r.isConfirmed) this._executeAccountParties(period, entityCode, modalRef);
      });
    }

    // Opciones de confirmación para personas
    private _accountPartiesConfirmOptions(entityCode: number) {
      const name = this.findEntityName(entityCode);
      return {
        title: '¿Exportar y Contabilizar?',
        text: `Se marcará como contabilizado el cobro de cada persona de ${entityCode}${name ? ' - ' + name : ''}.`,
        icon: 'question' as const,
        showCancelButton: true,
        confirmButtonText: 'Sí, Contabilizar',
        cancelButtonText: 'Cancelar'
      };
    }

    // Ejecuta contabilización de personas
    private _executeAccountParties(period: any, entityCode: number, modalRef?: any) {
      this.exportAsinetPartyVehicle(modalRef);
      this.billingService.markPartiesAccounted(period.uuid, entityCode).subscribe({
        next: () => this._onAccountSuccess('Los cobros de personas quedaron contabilizados.'),
        error: (e) => Swal.fire('Error', e.error?.error ?? 'Error al contabilizar', 'error')
      });
    }

    // Notifica éxito y recarga periodos
    private _onAccountSuccess(msg: string) {
      this.periodService.reload();
      this.loadBillings();
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: msg,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    }

    // Alerta periodo debe estar cerrado
    private _alertMustBeClosed() {
      Swal.fire('Atención', 'El periodo debe estar cerrado para contabilizar', 'warning');
    }
}

